import React, { useMemo } from 'react';

import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import type { AssetProcessingActivityPurposeApplication, AssetProcessingCountryRow } from 'config/api/assetRopa/assetRopa.types';
import { getRopaCountriesForUi, getRopaCountryClassification } from 'config/dictionaries/ropaCountryMasterdata';
import processing_activities_messages from 'messages/processing_activities_messages';

import useStyles from '../../ProcessingActivitiesAssetReviewsPage/ProcessingActivitiesAssetReviewsPage.styles';
import {
  APPLICABILITY_STATUS_OPTIONS,
  COUNTRY_VOLUME_OPTIONS,
  DPIA_INDICATION_STATUS_OPTIONS,
  MINIMUM_SECURITY_ASSESSMENT_OPTIONS,
  type AssetProcessingApaFormValues,
} from '../assetProcessingApa.types';

type SectionProps = {
  errors?: Partial<Record<string, string>>;
  onChange: (values: AssetProcessingApaFormValues) => void;
  readOnly?: boolean;
  values: AssetProcessingApaFormValues;
};

const getOptionLabel = (namespace: Record<string, string[]>, key: string, t: (message: string | string[]) => string) => {
  const message = namespace[key];
  return message ? t(message) : key.replace(/_/g, ' ');
};

export const AssetProcessingSecuritySection: React.FC<SectionProps> = ({ errors = {}, onChange, readOnly = false, values }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const messages = processing_activities_messages.asset_ropa.apa_editor.sections.security;

  return (
    <section className={styles.apaSection}>
      <Typography gutterBottom variant='h4'>
        {t(messages.title)}
      </Typography>
      <TextField
        disabled={readOnly}
        fullWidth
        label={t(messages.comment)}
        margin='normal'
        multiline
        onChange={event => onChange({ ...values, securityComment: event.target.value })}
        rows={4}
        value={values.securityComment}
        variant='outlined'
      />
      <FormControl disabled={readOnly} fullWidth margin='normal' variant='outlined'>
        <InputLabel>{t(messages.minimum_assessment)}</InputLabel>
        <Select
          label={t(messages.minimum_assessment)}
          onChange={event => onChange({ ...values, minimumSecurityAssessment: String(event.target.value) })}
          value={values.minimumSecurityAssessment}
        >
          {MINIMUM_SECURITY_ASSESSMENT_OPTIONS.map(option => (
            <MenuItem key={option} value={option}>
              {getOptionLabel(messages.minimum_assessment_options, option, t)}
            </MenuItem>
          ))}
        </Select>
        {errors.minimumSecurityAssessment && <FormHelperText error>{errors.minimumSecurityAssessment}</FormHelperText>}
      </FormControl>
      <TextField
        disabled={readOnly}
        error={Boolean(errors.minimumSecurityAssessmentComment)}
        fullWidth
        helperText={errors.minimumSecurityAssessmentComment}
        label={t(messages.minimum_assessment_comment)}
        margin='normal'
        multiline
        onChange={event => onChange({ ...values, minimumSecurityAssessmentComment: event.target.value })}
        rows={3}
        value={values.minimumSecurityAssessmentComment}
        variant='outlined'
      />
    </section>
  );
};

export const AssetProcessingDpiaSection: React.FC<SectionProps> = ({ errors = {}, onChange, readOnly = false, values }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const messages = processing_activities_messages.asset_ropa.apa_editor.sections.dpia;

  return (
    <section className={styles.apaSection}>
      <Typography gutterBottom variant='h4'>
        {t(messages.title)}
      </Typography>
      <FormControl disabled={readOnly} fullWidth margin='normal' variant='outlined'>
        <InputLabel>{t(messages.status)}</InputLabel>
        <Select
          label={t(messages.status)}
          onChange={event => onChange({ ...values, dpiaIndicationStatus: String(event.target.value) })}
          value={values.dpiaIndicationStatus}
        >
          {DPIA_INDICATION_STATUS_OPTIONS.map(option => (
            <MenuItem key={option} value={option}>
              {getOptionLabel(messages.status_options, option, t)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        disabled={readOnly}
        error={Boolean(errors.dpiaIndicationComment)}
        fullWidth
        helperText={errors.dpiaIndicationComment}
        label={t(messages.comment)}
        margin='normal'
        multiline
        onChange={event => onChange({ ...values, dpiaIndicationComment: event.target.value })}
        rows={3}
        value={values.dpiaIndicationComment}
        variant='outlined'
      />
    </section>
  );
};

type PurposeSectionProps = SectionProps & {
  centralPurposes: Array<{
    id: number;
    name: string;
    legalBasis: string;
    legalBasisComment: string;
    liaRequired: boolean;
    retentionRule: string;
  }>;
};

export const AssetProcessingPurposeApplicationsSection: React.FC<PurposeSectionProps> = ({
  centralPurposes,
  errors = {},
  onChange,
  readOnly = false,
  values,
}) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const messages = processing_activities_messages.asset_ropa.apa_editor.sections.purposes;

  const updatePurpose = (index: number, patch: Partial<AssetProcessingActivityPurposeApplication>) => {
    const next = values.purposeApplications.map((purpose, purposeIndex) => (purposeIndex === index ? { ...purpose, ...patch } : purpose));
    onChange({ ...values, purposeApplications: next });
  };

  return (
    <section className={styles.apaSection}>
      <Typography gutterBottom variant='h4'>
        {t(messages.title)}
      </Typography>
      {values.purposeApplications.map((purpose, index) => {
        const central = centralPurposes.find(item => item.id === purpose.processingPurposeId);
        return (
          <details key={purpose.id} className={styles.purposePanel} open>
            <summary className={styles.purposeSummary}>{purpose.name}</summary>
            <div className={styles.purposePanelBody}>
              {central && (
                <div className={styles.purposeCentralBlock}>
                  <Typography variant='h5'>{t(messages.central_definition)}</Typography>
                  <div className={styles.fieldList}>
                    <div>
                      <div className={styles.fieldLabel}>{t(messages.legal_basis)}</div>
                      <div>{central.legalBasis}</div>
                    </div>
                    <div>
                      <div className={styles.fieldLabel}>{t(messages.retention_rule)}</div>
                      <div>{central.retentionRule}</div>
                    </div>
                  </div>
                </div>
              )}
              <div className={styles.purposeAssetBlock}>
                <Typography variant='h5'>{t(messages.asset_application)}</Typography>
                <FormControl disabled={readOnly} fullWidth margin='normal' variant='outlined'>
                  <InputLabel>{t(messages.applicability)}</InputLabel>
                  <Select
                    label={t(messages.applicability)}
                    onChange={event =>
                      updatePurpose(index, {
                        applicabilityStatus: event.target.value as AssetProcessingActivityPurposeApplication['applicabilityStatus'],
                      })
                    }
                    value={purpose.applicabilityStatus}
                  >
                    {APPLICABILITY_STATUS_OPTIONS.map(option => (
                      <MenuItem key={option} value={option}>
                        {getOptionLabel(messages.applicability_options, option, t)}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors[`purpose.${index}.applicabilityStatus`] && (
                    <FormHelperText error>{errors[`purpose.${index}.applicabilityStatus`]}</FormHelperText>
                  )}
                  {errors[`purpose.${index}.applies`] && <FormHelperText error>{errors[`purpose.${index}.applies`]}</FormHelperText>}
                </FormControl>
                {purpose.applicabilityStatus === 'applies' && (
                  <>
                    <TextField
                      disabled={readOnly}
                      fullWidth
                      label={t(messages.justification)}
                      margin='normal'
                      multiline
                      onChange={event => updatePurpose(index, { justification: event.target.value })}
                      rows={2}
                      value={purpose.justification}
                      variant='outlined'
                    />
                    <TextField
                      disabled={readOnly}
                      fullWidth
                      label={t(messages.retention_trigger)}
                      margin='normal'
                      onChange={event => updatePurpose(index, { retentionTrigger: event.target.value })}
                      value={purpose.retentionTrigger}
                      variant='outlined'
                    />
                    <TextField
                      disabled={readOnly}
                      fullWidth
                      label={t(messages.retention_value)}
                      margin='normal'
                      onChange={event => updatePurpose(index, { retentionValue: Number(event.target.value) || null })}
                      type='number'
                      value={purpose.retentionValue ?? ''}
                      variant='outlined'
                    />
                    <TextField
                      disabled={readOnly}
                      fullWidth
                      label={t(messages.retention_unit)}
                      margin='normal'
                      onChange={event => updatePurpose(index, { retentionUnit: event.target.value })}
                      value={purpose.retentionUnit}
                      variant='outlined'
                    />
                    <TextField
                      disabled={readOnly}
                      fullWidth
                      label={t(messages.deletion_routine)}
                      margin='normal'
                      multiline
                      onChange={event => updatePurpose(index, { deletionRoutine: event.target.value })}
                      rows={2}
                      value={purpose.deletionRoutine}
                      variant='outlined'
                    />
                  </>
                )}
              </div>
            </div>
          </details>
        );
      })}
    </section>
  );
};

export const AssetProcessingCountryRowsSection: React.FC<SectionProps> = ({ errors = {}, onChange, readOnly = false, values }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const messages = processing_activities_messages.asset_ropa.apa_editor.sections.countries;
  const countries = useMemo(() => getRopaCountriesForUi(), []);

  const updateRow = (index: number, patch: Partial<AssetProcessingCountryRow>) => {
    const next = values.countryRows.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row));
    onChange({ ...values, countryRows: next });
  };

  const addRow = () => {
    onChange({
      ...values,
      countryRows: [
        ...values.countryRows,
        {
          id: Date.now(),
          countryCode: '',
          countryName: '',
          classification: '',
          storage: false,
          access: false,
          support: false,
          development: false,
          volume: '',
        },
      ],
    });
  };

  const removeRow = (index: number) => {
    onChange({ ...values, countryRows: values.countryRows.filter((_, rowIndex) => rowIndex !== index) });
  };

  return (
    <section className={styles.apaSection}>
      <Typography gutterBottom variant='h4'>
        {t(messages.title)}
      </Typography>
      {values.countryRows.map((row, index) => (
        <div key={row.id} className={styles.countryRowCard}>
          <FormControl disabled={readOnly} fullWidth margin='normal' variant='outlined'>
            <InputLabel>{t(messages.country_name)}</InputLabel>
            <Select
              label={t(messages.country_name)}
              onChange={event => {
                const countryCode = String(event.target.value);
                const country = countries.find(item => item.iso2 === countryCode);
                updateRow(index, {
                  countryCode,
                  countryName: country?.name ?? countryCode,
                  classification: getRopaCountryClassification(countryCode),
                });
              }}
              value={row.countryCode}
            >
              {countries.map(country => (
                <MenuItem key={country.iso2} value={country.iso2}>
                  {country.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormGroup row>
            {(['storage', 'access', 'support', 'development'] as const).map(flag => (
              <FormControlLabel
                key={flag}
                control={
                  <Checkbox
                    checked={row[flag]}
                    disabled={readOnly}
                    onChange={event => updateRow(index, { [flag]: event.target.checked })}
                  />
                }
                label={t(messages.handling[flag])}
              />
            ))}
          </FormGroup>
          <FormControl disabled={readOnly} fullWidth margin='normal' variant='outlined'>
            <InputLabel>{t(messages.volume)}</InputLabel>
            <Select
              label={t(messages.volume)}
              onChange={event => updateRow(index, { volume: String(event.target.value) })}
              value={row.volume}
            >
              {COUNTRY_VOLUME_OPTIONS.map(option => (
                <MenuItem key={option} value={option}>
                  {getOptionLabel(messages.volume_options, option, t)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {errors[`country.${index}`] && <FormHelperText error>{errors[`country.${index}`]}</FormHelperText>}
          {!readOnly && (
            <button className={styles.textButtonLink} onClick={() => removeRow(index)} type='button'>
              {t(messages.remove_row)}
            </button>
          )}
        </div>
      ))}
      {!readOnly && (
        <button className={styles.textButtonLink} onClick={addRow} type='button'>
          {t(messages.add_row)}
        </button>
      )}
    </section>
  );
};
